import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { FiBookOpen, FiCheck, FiClock, FiPlus, FiTrash2, FiUser, FiX } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import fnc from '../../mymod/commFunction';
import { setIsRefresh_studentCourseList } from '../../reduxToolkit/redux';
import './CourseRegistrationPanel.scss';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '2.85rem',
    borderColor: state.isFocused ? '#7764ed' : '#e3e5ed',
    borderRadius: '0.8rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(119, 100, 237, 0.12)' : 'none',
    fontSize: '0.76rem',
    fontWeight: 700,
  }),
  menu: (base) => ({ ...base, zIndex: 50, overflow: 'hidden', borderRadius: '0.75rem' }),
  menuList: (base) => ({ ...base, maxHeight: '11rem', padding: '0.3rem' }),
  option: (base, state) => ({
    ...base,
    overflow: 'hidden',
    borderRadius: '0.55rem',
    background: state.isSelected ? '#6956e8' : state.isFocused ? '#f0edff' : '#fff',
    color: state.isSelected ? '#fff' : '#303950',
    fontSize: '0.74rem',
    fontWeight: 700,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  singleValue: (base) => ({ ...base, color: '#303950' }),
  placeholder: (base) => ({ ...base, color: '#969dad' }),
};

const CourseRegistrationPanel = ({ className = '' }) => {
  const dispatch = useDispatch();
  const refreshToken = useSelector((state) => state.reduxStore.isRefresh_studentCourseList);
  const [instructors, setInstructors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [pendingKey, setPendingKey] = useState('');
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const catalogRequest = useRef(0);

  const acaCode = fnc.getCookie('acaCode');
  const stCode = fnc.getCookie('stCode');

  const instructorOptions = useMemo(
    () => instructors.map((item) => ({ value: item.gangsaCode, label: item.name })),
    [instructors],
  );

  const groupOptions = useMemo(
    () => groups.map((item) => ({ value: item.groupCode, label: item.groupName })),
    [groups],
  );

  const enrolledCodeSet = useMemo(
    () => new Set(enrolledCourses.map((item) => String(item.classCode))),
    [enrolledCourses],
  );

  const loadEnrolledCourses = () => {
    fnc.sendAxios('/select/class/studentCourseList', { acaCode, stCode }, (res) => {
      setEnrolledCourses(Array.isArray(res) ? res : []);
    });
  };

  useEffect(() => {
    fnc.sendAxios('/select/class/gangsaList', { acaCode }, (res) => {
      setInstructors(Array.isArray(res) ? res : []);
    });
  }, [acaCode]);

  useEffect(() => {
    loadEnrolledCourses();
  }, [refreshToken]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const refreshLmsCourses = () => {
    dispatch(setIsRefresh_studentCourseList(!refreshToken));
  };

  const handleInstructorChange = (option) => {
    const request = ++catalogRequest.current;
    setSelectedInstructor(option);
    setSelectedGroup(null);
    setGroups([]);
    setAvailableCourses([]);
    setIsLoadingCourses(false);
    setMessage('');

    if (!option) return;
    fnc.sendAxios(
      '/select/class/groupList',
      { acaCode, gangsaCode: option.value },
      (res) => {
        if (request === catalogRequest.current) setGroups(Array.isArray(res) ? res : []);
      },
    );
  };

  const handleGroupChange = (option) => {
    const request = ++catalogRequest.current;
    setSelectedGroup(option);
    setAvailableCourses([]);
    setMessage('');
    setIsLoadingCourses(false);
    if (!option || !selectedInstructor) return;

    setIsLoadingCourses(true);
    fnc.sendAxios('/select/class/classList', { acaCode, groupCode: option.value, gangsaCode: selectedInstructor.value }, (res) => {
      if (request !== catalogRequest.current) return;
      setAvailableCourses(Array.isArray(res) ? res : []);
      setIsLoadingCourses(false);
    });
  };

  const handleRegister = (course) => {
    if (enrolledCodeSet.has(String(course.classCode))) {
      setMessage('이미 신청된 수업입니다.');
      return;
    }

    if (!window.confirm(`${course.className} 수업을 신청하시겠습니까?`)) return;
    setPendingKey(`register-${course.classCode}`);
    setMessage('');

    fnc.sendAxios(
      '/insert/class/cl_course',
      {
        acaCode,
        stCode,
        classCode: course.classCode,
        className: course.className,
        sDate: fnc.fncDate(),
      },
      (res) => {
        setPendingKey('');
        if (res?.success === 'exist') {
          setMessage('이미 신청된 수업입니다.');
          loadEnrolledCourses();
          return;
        }

        setMessage('수강신청이 완료되었습니다.');
        refreshLmsCourses();
      },
    );
  };

  const handleCancel = (course) => {
    if (!window.confirm(`${course.className} 수강신청을 취소하시겠습니까?`)) return;
    setPendingKey(`cancel-${course.sno}`);
    setMessage('');

    fnc.sendAxios('/delete/class/student_course', { acaCode, sno: course.sno }, (res) => {
      setPendingKey('');
      if (!res) return;
      setMessage('수강신청이 취소되었습니다.');
      refreshLmsCourses();
    });
  };

  const modal = isOpen ? (
    <div
      className="course-registration-modal"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setIsOpen(false);
      }}
    >
      <div
        className="course-registration-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="course-registration-title"
      >
        <header className="course-registration-modal__header">
          <span><FiBookOpen aria-hidden="true" /></span>
          <div>
            <h2 id="course-registration-title">수강신청</h2>
            <p>선생님과 과정을 선택해 수업을 신청하세요.</p>
          </div>
          <em>{enrolledCourses.length}</em>
          <button type="button" onClick={() => setIsOpen(false)} aria-label="수강신청 닫기" autoFocus>
            <FiX aria-hidden="true" />
          </button>
        </header>

        <section className="course-registration-panel">
          <div className="course-registration-panel__filters">
            <Select
              className="course-registration-select"
              classNamePrefix="course-registration-select"
              options={instructorOptions}
              value={selectedInstructor}
              onChange={handleInstructorChange}
              placeholder="선생님 선택"
              noOptionsMessage={() => '선생님이 없습니다.'}
              isClearable
              styles={selectStyles}
            />
            <Select
              className="course-registration-select"
              classNamePrefix="course-registration-select"
              options={groupOptions}
              value={selectedGroup}
              onChange={handleGroupChange}
              placeholder="과정 선택"
              noOptionsMessage={() => selectedInstructor ? '과정이 없습니다.' : '선생님을 먼저 선택하세요.'}
              isDisabled={!selectedInstructor}
              isClearable
              styles={selectStyles}
            />
          </div>

          {message && <p className="course-registration-panel__message" role="status">{message}</p>}

          <div className="course-registration-panel__content">
            <section className="course-registration-panel__available">
              <h3>신청 가능한 수업</h3>
              {!selectedGroup && <p className="course-registration-panel__empty">과정을 선택하면 수업이 표시됩니다.</p>}
              {selectedGroup && isLoadingCourses && <p className="course-registration-panel__empty">수업을 불러오는 중입니다.</p>}
              {selectedGroup && !isLoadingCourses && availableCourses.length === 0 && (
                <p className="course-registration-panel__empty">신청 가능한 수업이 없습니다.</p>
              )}
              {availableCourses.map((course) => {
                const isEnrolled = enrolledCodeSet.has(String(course.classCode));
                const isPending = pendingKey === `register-${course.classCode}`;

                return (
                  <article className="course-registration-course" key={course.classCode}>
                    <span className="course-registration-course__icon"><FiBookOpen aria-hidden="true" /></span>
                    <div>
                      <strong>{course.className}</strong>
                      <small>{course.sDate || '수강일 협의'}</small>
                    </div>
                    <button
                      type="button"
                      className={isEnrolled ? 'is-enrolled' : ''}
                      onClick={() => handleRegister(course)}
                      disabled={isEnrolled || isPending}
                    >
                      {isEnrolled ? <FiCheck aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                      {isEnrolled ? '신청됨' : isPending ? '처리 중' : '신청'}
                    </button>
                  </article>
                );
              })}
            </section>

            <section className="course-registration-panel__enrolled">
              <h3>내 수강 신청</h3>
              {enrolledCourses.length === 0 && (
                <p className="course-registration-panel__empty">신청한 수업이 없습니다.</p>
              )}
              {enrolledCourses.map((course) => (
                <article className="course-registration-course is-current" key={course.sno}>
                  <span className="course-registration-course__icon">
                    {course.status === 'Y' ? <FiCheck aria-hidden="true" /> : <FiClock aria-hidden="true" />}
                  </span>
                  <div>
                    <strong>{course.className}</strong>
                    <small><FiUser aria-hidden="true" /> {course.gangSa || '담당 선생님'} · {course.status === 'Y' ? '수강 중' : '승인 대기'}</small>
                  </div>
                  <button
                    type="button"
                    className="is-cancel"
                    onClick={() => handleCancel(course)}
                    disabled={pendingKey === `cancel-${course.sno}`}
                    aria-label={`${course.className} 수강신청 취소`}
                  >
                    <FiTrash2 aria-hidden="true" />
                    취소
                  </button>
                </article>
              ))}
            </section>
          </div>
        </section>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className={`course-registration-entry ${className}`.trim()}>
        <button
          type="button"
          className="course-registration-launcher"
          onClick={() => {
            loadEnrolledCourses();
            setIsOpen(true);
          }}
        >
          <FiPlus aria-hidden="true" />
          <span>수강신청하기</span>
          {enrolledCourses.length > 0 && <em>{enrolledCourses.length}</em>}
        </button>
      </div>
      {modal && createPortal(modal, document.body)}
    </>
  );
};

export default CourseRegistrationPanel;
